import { datadogLogs } from '@datadog/browser-logs'
import { isOnServer } from '@shared/constants/util'
import { classifyError, shouldReportError } from '@/lib/datadog/errorGrouping'
import { toError } from '@/features/shared/utils/dataFetching/utils'
import { type LogMeta } from '@/features/shared/utils/logger/types'

/**
 * Logs errors to Datadog Logs with automatic error classification.
 *
 * - Filters globally ignored error messages
 * - Classifies errors (React Hydration, Chunk Failure, DOM Errors, etc.)
 * - Sends to Datadog Logs API with full stack trace and metadata
 *
 * @param err - The error object or error message
 * @param meta - Additional metadata including optional customGroupingHash for error grouping
 * @param options - Additional error options (e.g., ErrorOptions)
 *
 * @example
 * import { datadogErrorHandler } from '@/lib/datadog/errorHandler';
 *
 * datadogErrorHandler(error, {
 *   endpoint: '/api/products',
 *   statusCode: 500,
 *   customGroupingHash: 'api_error_products'
 * });
 */
export function datadogErrorHandler(
  err: unknown,
  meta: LogMeta = {},
  options?: ErrorOptions,
): void {
  if (isOnServer()) return

  const error = toError(err, options)
  const message = error.message || String(err)

  if (!shouldReportError(message, error.stack)) {
    return
  }

  const classification = classifyError(error)
  const logPayload = {
    error: {
      kind: error.name,
      message: error.message,
      stack: error.stack,
    },
    metadata: {
      ...meta,
      errorClassification: classification.type,
      ...classification.tags,
    },
  }

  datadogLogs.logger.error(message, logPayload)
}

/**
 * Logs warning messages to Datadog Logs.
 * Use for concerning but non-fatal issues (e.g., fallbacks, degraded behavior).
 *
 * @param message - The warning message
 * @param meta - Additional warning context metadata
 *
 * @example
 * import { datadogWarn } from '@/lib/datadog/errorHandler';
 *
 * datadogWarn('Failed to load cached data, using network fallback', {
 *   cacheKey: 'user_preferences',
 *   fallbackUsed: true
 * });
 */
export function datadogWarn(message: string, meta: LogMeta = {}): void {
  if (isOnServer()) return

  datadogLogs.logger.warn(message, {
    metadata: {
      ...meta,
    },
  })
}

/**
 * Logs informational messages to Datadog Logs.
 * Use for application flow tracking, successful operations, and general diagnostics.
 *
 * @param message - The info message
 * @param meta - Additional metadata for context
 *
 * @example
 * import { datadogInfo } from '@/lib/datadog/errorHandler';
 *
 * datadogInfo('User successfully logged in', {
 *   userId: user.id,
 *   loginMethod: 'email'
 * });
 */
export function datadogInfo(message: string, meta: LogMeta = {}): void {
  if (isOnServer()) return

  datadogLogs.logger.info(message, {
    metadata: {
      ...meta,
    },
  })
}

/**
 * Logs debug messages to Datadog Logs for development diagnostics.
 * Use for detailed diagnostic information during development and troubleshooting.
 *
 * - Lower priority than info logs
 * - Useful for tracking specific flow checkpoints and data states
 * - Helps with debugging API responses, data transformations, etc.
 *
 * @param message - The debug message
 * @param meta - Additional debugging metadata
 *
 * @example
 * import { datadogDebug } from '@/lib/datadog/errorHandler';
 *
 * datadogDebug('API response received', {
 *   endpoint: '/api/user',
 *   responseTime: 150,
 *   dataSize: 2048
 * });
 */
export function datadogDebug(message: string, meta: LogMeta = {}): void {
  if (isOnServer()) return

  datadogLogs.logger.debug(message, {
    metadata: {
      ...meta,
    },
  })
}
